import type { SlideType } from "../lib/types";

/** The eight type marks and the wordmark, ported from the HTML prototype. */

interface IconProps {
  size?: number;
  color?: string;
}

const PATHS: Record<SlideType, React.ReactNode> = {
  multiple: (
    <>
      <rect x="4" y="12" width="4" height="8" rx="1" />
      <rect x="10" y="7" width="4" height="13" rx="1" />
      <rect x="16" y="3" width="4" height="17" rx="1" />
    </>
  ),
  wordcloud: <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />,
  scale: (
    <>
      <line x1="4" y1="8" x2="20" y2="8" />
      <circle cx="9" cy="8" r="2.4" fill="currentColor" stroke="none" />
      <line x1="4" y1="16" x2="20" y2="16" />
      <circle cx="16" cy="16" r="2.4" fill="currentColor" stroke="none" />
    </>
  ),
  open: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  guess: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 2.6-3 4" />
      <line x1="12" y1="17.5" x2="12" y2="17.6" />
    </>
  ),
  quiz: <path d="M13 2 4.1 12.6a.6.6 0 0 0 .5 1h6l-1.6 8 8.9-10.6a.6.6 0 0 0-.5-1h-6z" />,
  ranking: (
    <>
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="15" y2="12" />
      <line x1="4" y1="18" x2="10" y2="18" />
    </>
  ),
  grid: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="1.5" />
      <rect x="13" y="4" width="7" height="7" rx="1.5" />
      <rect x="4" y="13" width="7" height="7" rx="1.5" />
      <rect x="13" y="13" width="7" height="7" rx="1.5" />
    </>
  ),
};

export function SlideIcon({
  type,
  size = 20,
  color = "currentColor",
}: IconProps & { type: SlideType }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={color}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: "none", color }}
      aria-hidden="true"
    >
      {PATHS[type]}
    </svg>
  );
}

export function Logo({ size = 26 }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path
        d="M2 9c2-2.6 3.5-2.6 5.5 0s3.5 2.6 5.5 0 3.5-2.6 5.5 0 2.8 2 3.5 1.2"
        stroke="#5FA8BD"
      />
      <path
        d="M2 15c2-2.6 3.5-2.6 5.5 0s3.5 2.6 5.5 0 3.5-2.6 5.5 0 2.8 2 3.5 1.2"
        stroke="#33708C"
      />
    </svg>
  );
}

export function PlayIcon({ size = 16 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M7 4.5v15l13-7.5z" />
    </svg>
  );
}

export function Spinner({ size = 18 }: IconProps) {
  return (
    <span
      role="status"
      aria-label="Loading"
      style={{
        width: size,
        height: size,
        display: "inline-block",
        border: "2px solid var(--outline)",
        borderTopColor: "var(--primary)",
        borderRadius: "50%",
        animation: "spin 700ms linear infinite",
      }}
    />
  );
}

/** Slide n is tinted with hue n % 8 — how a deck stays readable at a glance.
 *  Use hueFor for fills and hueInk for text; see the note in tokens.css. */
export function hueFor(index: number): string {
  return `var(--hue-${index % 8})`;
}

export function hueInk(index: number): string {
  return `var(--hue-${index % 8}-ink)`;
}
